import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";
import useEntityStore from "../../stores/entityStore";
import { resolveEntityId } from "./entityIdResolver";

const log = createFileLogger('ENTITY_UPDATE');

export const entityUpdateCommand: SelfContainedCommand = {
  id: 'entity_update',
  description: `Update an existing entity with new data. Can update name, skeleton, metadata, or any other entity properties.

**SUPPORTED ENTITIES:**
- Simple entities (Product, APIConnection, Integration, custom types)
- Y.js entities are BLOCKED - use specialized commands instead

**IMPORTANT FOR PRODUCT ENTITIES:**
- Product entities do NOT support entity_read - update directly without reading first
- Just call entity_update with the data you want to merge - it will be merged into existing skeleton
- The backend uses MongoDB $set with dot notation for safe atomic updates

**Use Cases:**
- Add Stripe Price ID to Product
- Update Product price/stock
- Update APIConnection credentials
- Rename entities
- Update any skeleton/metadata fields

**EXAMPLES:**

Update Product skeleton (merges with existing fields):
{ "entityId": "product_abc", "data": { "skeleton": { "price": 8999, "inStock": true } } }

Add Stripe IDs to Product (preserves other skeleton fields):
{ "entityId": "product_abc", "data": { "skeleton": { "stripeProductId": "prod_123", "stripePriceId": "price_123" } } }

Rename entity:
{ "entityId": "product_abc", "data": { "name": "New Product Name" } }`,
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'The ID of the entity to update',
        required: true
      },
      {
        name: 'data',
        type: 'object',
        description: 'The data to update the entity with',
        required: true
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { entityId: 'doc_123', data: { name: 'Updated Document Name' } },
        expectedOutput: createSuccessResult('Entity updated successfully', {
          entity: { _id: 'doc_123', name: 'Updated Document Name' }
        }),
        description: 'Rename an entity',
        context: 'When user wants to change entity name'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 300,
    complexity: 'low'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    let { entityId, data } = params;

    if (!data || Object.keys(data).length === 0) {
      return createErrorResult(
        'No updates provided',
        'data must contain at least one field to update',
        'validation'
      );
    }
    
    try {
      // ✅ CRITICAL: Resolve llmId to _id if needed
      const resolution = resolveEntityId(entityId);
      
      if (resolution.isLlmId && !resolution.entity) {
        return createErrorResult(
          'Entity not found',
          `No entity found with llmId: ${entityId}`,
          'validation',
          { data: { providedId: entityId } }
        );
      }
      
      entityId = resolution.resolved;
      let entity = resolution.entity;

      // Fallback: if entityId was a MongoDB _id (not an llmId), entity won't be populated
      // by resolveEntityId — look it up directly from the store so the Y.js guard works.
      if (!entity) {
        const entityStore = useEntityStore.getState();
        entity = entityStore.entities.find(e => e._id === entityId);
      }
      
      // Block Y.js entities - they need specialized commands
      const yjsEntityTypes = ['Doc', 'app_project', 'website_project', 'Note', 'Sheet', 'Canvas', 'Presentation'];
      if (entity && yjsEntityTypes.includes(entity.entityType)) {
        return createErrorResult(
          `Cannot use entity_update for ${entity.entityType}`,
          `This entity uses Y.js substrate. Use specialized commands instead:
          - Doc: use doc_write
          - app_project/website_project: use code_write
          - Note: use note_write
          - Sheet: use sheet_write
          - Canvas: use canvas_write
          - Presentation: use slide_write`,
          'validation'
        );
      }
      
      log.info('[entity_update] Updating entity:', JSON.stringify({ 
        originalId: params.entityId,
        resolvedId: entityId, 
        wasLlmId: resolution.isLlmId,
        entityType: entity?.entityType,
        fields: Object.keys(data)
      }));
      
      const entityStore = useEntityStore.getState();
      const updatedEntity = await entityStore.actions.updateEntity(entityId, data);
      
      log.info('[entity_update] ✅ Entity updated:', { id: updatedEntity._id });
      
      // Create a descriptive success message
      let message = 'Entity updated successfully';
      if (data.name) {
        message = `Renamed to "${data.name}"`;
      } else {
        const fieldCount = Object.keys(data).length;
        message = `Updated ${fieldCount} field${fieldCount !== 1 ? 's' : ''}`;
      }
      
      return createSuccessResult(
        message,
        { entity: updatedEntity },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'entityStore.actions',
            affectedEntities: [entityId]
          } as any,
          ui: {
            refreshContext: true
          }
        }
      );
      
    } catch (error: any) {
      log.error('[entity_update] Error:', error);
      return createErrorResult(
        'Entity update failed',
        `Failed to update entity: ${error.message || 'Unknown error'}`,
        'execution',
        {
          data: { entityId },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

